import { useState, useEffect, useCallback, useRef } from 'react';

const useInfiniteScroll = ({
  fetchFunction,
  pageSize = 10,
  dependencies = [],
  scrollOffset = 50,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [MoQData, setMoQData] = useState(null);
  const loadingRef = useRef(false);

  const fetchData = useCallback(
    async (page = 1, append = false, force = false) => {
      if (loadingRef.current && !force) return;
      loadingRef.current = true;
      setLoading(true);

      try {
        const result = await fetchFunction(page, pageSize);

        if (result && !result.error) {
          const newData = result.data || [];
          if (Array.isArray(newData)) {
            setData((prev) => (append ? [...prev, ...newData] : newData));
            setHasMore(newData.length === pageSize);
          } else if (newData.po_list) {
            const poList = newData.po_list || [];
            setData((prev) => (append ? [...prev, ...poList] : poList));
            setMoQData(newData?.min_max_moq_and_mov || null);
            setHasMore(poList.length === pageSize);
          } else {
            setData((prev) => (append ? { ...prev, ...newData } : newData));
            setHasMore(false);
          }
          if (append) {
            setPageNumber(page);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [fetchFunction, pageSize],
  );

  const loadMore = useCallback(() => {
    if (hasMore && !loading) {
      const nextPage = pageNumber + 1;
      fetchData(nextPage, true);
    }
  }, [hasMore, loading, pageNumber, fetchData]);

  const resetData = useCallback(() => {
    setData([]);
    setPageNumber(1);
    setHasMore(true);
    setLoading(false);
    loadingRef.current = false;
  }, []);

  const refetchData = useCallback(() => {
    resetData();
    setTimeout(() => {
      fetchData(1, false, true);
    }, 0);
  }, [resetData, fetchData]);

  // Auto load more on scroll
  useEffect(() => {
    let timeoutId;

    const handleScroll = () => {
      // Debounce scroll events for better performance on desktop
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight =
          window.innerHeight || document.documentElement.clientHeight;
        const documentHeight = Math.max(
          document.body.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.clientHeight,
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight,
        );

        // More aggressive loading for desktop - 70% threshold
        const scrollPercentage = (scrollTop + windowHeight) / documentHeight;
        const shouldLoad = scrollPercentage >= 0.7;

        if (shouldLoad && hasMore && !loading) {
          loadMore();
        }
      }, 50); // 50ms debounce for desktop scroll events
    };

    // Add both scroll and wheel events for better desktop coverage
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("wheel", handleScroll, { passive: true });

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("wheel", handleScroll);
    };
  }, [hasMore, loading, loadMore]);

  // Initial fetch and refetch on dependencies change
  useEffect(() => {
    if (dependencies.length === 0) {
      resetData(); // no access / not ready -> clear stale data, do not call the API
      return;
    }
    refetchData();
  }, dependencies);

  return {
    MoQData,
    data,
    loading,
    hasMore,
    pageNumber,
    loadMore,
    refetchData,
    resetData,
  };
};;

export default useInfiniteScroll;